<template>
  <div
      class="product"
  >
    <div class="product__left">
      <img class="product__image" :src="product.images.edges[0]?.node.image" :alt="product.name">
      <router-link :to="translations.i18nRoute({ name: 'product', params: { productSlug: product.slug } })" class="product__name">{{ product.name }}</router-link>
    </div>
    <div class="product__right">
      <div class="product__tools">
        <p>{{ productQuantity }}</p>
        <div class="product__tools-button" @click="addProductToCart">+</div>
        <div class="product__tools-button" @click="removeProductFormCart">-</div>
      </div>
      <div class="product__price">
        <span style="color: #3b3b3b">€</span>
        <p>{{ product.price }}</p>
      </div>
    </div>

    <svg @click="removeAllProductFormCart" class="product__delete" width="44" height="43" viewBox="0 0 44 43" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="21.2129" r="15" fill="currentColor"/>
      <path d="M17.2988 24.6051L20.92 20.9839L17.2988 17.3628L18.5998 16.0618L22.221 19.683L25.8504 16.0535L27.1514 17.3545L23.5219 20.9839L27.1514 24.6134L25.8504 25.9144L22.221 22.2849L18.5998 25.9061L17.2988 24.6051Z"
            fill="white"/>
    </svg>
  </div>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {computed} from "vue";
import {useCartStore} from "@/stores/cart.js";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {useRouter} from "vue-router";
import {Orders} from "@/core/api/orders.js";
import {useI18n} from "vue-i18n";

const props = defineProps({
  product: Object
})

const cartStore = useCartStore()
const authStore = useAuthStore()
const router = useRouter()
const {t} = useI18n()

const productQuantity = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.filter((prod) => prod.node.product.uuid === props.product.uuid)[0].node.quantity
})

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})

const addProductToCart = async () => {
  if (user.value) {
    const isProductInCart = cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)

    if (isProductInCart) {
      if (props.product.quantity > productQuantity.value) {
        const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
        cartStore.setCurrentOrders(res.data.addOrderProduct.order)

        if (res.errors) {
          ElNotification({
            title: t('popup.error'),
            message: res.errors[0].message,
            type: 'error'
          })
        } else {
          ElNotification({
            message: t('popup.addToCart', { product: props.product.name }),
            type: 'success'
          })
        }
      } else {
        ElNotification({
          title: t('popup.error'),
          message: t('popup.addToCartLimit', { quantity: props.product.quantity }),
          type: 'error'
        })
      }
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

      if (res.errors) {
        ElNotification({
          title: t('popup.error'),
          message: res.errors[0].message,
          type: 'error'
        })
      } else {
        cartStore.setCurrentOrders(res.data.addOrderProduct?.order)

        ElNotification({
          message: t('popup.addToCart', { product: props.product.name }),
          type: 'success'
        })
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
      }
    })
  }
}
const removeProductFormCart = async () => {
  if (productQuantity.value === 1) {
    await removeAllProductFormCart()
    return
  }

  const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

  if (!res.errors) {
    cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
  }
}
const removeAllProductFormCart = async () => {
  const res = await Orders.overwriteCart("removeOrderProductsOfAKind", accessToken.value, orderUuid.value, props.product.uuid);
  if (!res.errors) {
    cartStore.setCurrentOrders(res.data.removeOrderProductsOfAKind.order)
  }
}
</script>

<style lang="scss" scoped>
.product {
  border-radius: 16px;
  border: 1px solid #3b3b3b;
  background-color: $white;
  box-shadow: 0 4px 7.5px 0 rgba(0, 0, 0, 0.10);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 15px 20px;
  position: relative;

  &__left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  &__image {
    width: 125px;
    height: 70px;
    object-fit: cover;
    border-radius: 15px;
  }

  &__name {
    transition: 0.2s;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #222;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;

    @include hover {
      color: #3b3b3b;
    }
  }

  &__tools {
    display: flex;
    align-items: center;
    gap: 5px;

    & p {
      color: #000000;
      font-size: 22px;
      font-weight: 600;
      line-height: 25px;
    }

    &-button {
      cursor: pointer;
      border-radius: 3px;
      background-color: #FFFFFF;
      width: 25px;
      height: 25px;
      display: grid;
      place-items: center;
      transition: 0.2s;

      color: #3b3b3b;
      text-align: center;
      font-size: 22px;
      font-weight: 600;
      line-height: 25px;

      @include hover {
        background-color: #3b3b3b;
        color: $white;
      }
    }
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 5px;

    & img {
      width: 20px;
    }

    & p {
      color: #000000;
      font-size: 22px;
      font-weight: 700;
      line-height: 25px;
    }
  }

  &__delete {
    position: absolute;
    top: -20px;
    right: -20px;
    cursor: pointer;
    transition: 0.2s;
    color: #3b3b3b;

    @include hover {
      color: #DD6196;
    }
  }
}

@media (max-width: 600px) {
  .product {
    &__left {
      flex-direction: column;
      align-items: flex-start;
    }

    &__right {
      flex-direction: column;
      align-items: flex-end;
    }
  }
}
</style>